// NodesArrayViewer.js
import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Tooltip,
  Alert,
  AlertTitle,
  Chip,
} from "@mui/material";
import { buildNumberedListFromArray } from "./nodeUtils";

/**
 * Status palette. Each status has a text colour, a soft background and a border.
 * The label is always shown in a chip so colour is never the only signal.
 */
const STATUS_STYLES = {
  success: {
    label: "Success",
    color: "#2e7d32",
    bg: "#e8f5e9",
    border: "#a5d6a7",
  },
  failed: {
    label: "Failed",
    color: "#c62828",
    bg: "#fdecea",
    border: "#ef9a9a",
  },
  dryRun: {
    label: "Dry run",
    color: "#1565c0",
    bg: "#e3f2fd",
    border: "#90caf9",
  },
  pending: {
    label: "Awaiting approval",
    color: "#6a1b9a",
    bg: "#f3e5f5",
    border: "#ce93d8",
  },
  planned: {
    label: "Planned",
    color: "#00695c",
    bg: "#e0f2f1",
    border: "#80cbc4",
  },
  skipped: {
    label: "Skipped",
    color: "#616161",
    bg: "#f5f5f5",
    border: "#e0e0e0",
  },
};

// Order used for the summary chips at the top
const STATUS_ORDER = [
  "success",
  "failed",
  "dryRun",
  "pending",
  "planned",
  "skipped",
];

/**
 * Work out which status a node belongs to.
 * Priority: failed > dry run > awaiting approval > planned > success > skipped
 */
const getStatus = (node, fallbackText = "") => {
  const msg = String(node?.msg ?? fallbackText ?? "").toLowerCase();
  const output = String(node?.output ?? "").toLowerCase();

  if (node?.error || output === "failed") return "failed";
  if (msg.includes("dry run")) return "dryRun";
  if (msg.startsWith("intent once approved")) return "pending";
  if (msg.startsWith("going to")) return "planned";
  if (output === "success") return "success";
  return "skipped";
};

const PaperWrapper = ({ children }) => (
  <Paper
    sx={{
      p: 1.5,
      mb: 1.5,
      background: "#f9f9ff",
      border: "1px solid #e0e0e0",
    }}
    elevation={1}
  >
    <Typography
      variant="subtitle1"
      sx={{ fontWeight: 600, px: 1, pt: 0.5, pb: 1 }}
    >
      Filing Summary
    </Typography>
    {children}
  </Paper>
);

/**
 * NodesArrayViewer
 * Renders an array of nodes without requiring a root node wrapper
 *
 * Props:
 *  - nodes: array of node objects to render
 *  - selectedCategory: optional category filter
 *  - nodeConfigs: used to format templates
 *  - onSelectNode(id) - called when clicking the rule number
 *  - selectedNodeId - currently selected node id
 */
const NodesArrayViewer = ({
  nodes = [],
  selectedCategory = null,
  nodeConfigs = {},
  onSelectNode,
  selectedNodeId,
}) => {
  const lines = buildNumberedListFromArray(nodes, nodeConfigs);

  // Drop lines whose text is missing
  const visibleLines = useMemo(
    () =>
      (lines || []).filter((line) => {
        if (line.isOutcomeHeader) return true;
        const t = String(line.text ?? "").trim();
        return t !== "" && !/\bundefined\b/i.test(t);
      }),
    [lines],
  );
  const hasRunNodes = visibleLines.some((l) => !l.isOutcomeHeader);

  // Count statuses for the summary chips
  const statusCounts = useMemo(() => {
    const counts = {};
    visibleLines.forEach((line) => {
      if (line.isOutcomeHeader) return;
      const status = getStatus(line.node, line.text);
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [visibleLines]);

  if (!nodes || nodes.length === 0 || !hasRunNodes) {
    return (
      <PaperWrapper>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", py: 2 }}
        >
          No action nodes run.
        </Typography>
      </PaperWrapper>
    );
  }

  return (
    <PaperWrapper>
      {/* Summary chips */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, px: 1, pb: 1 }}>
        {STATUS_ORDER.filter((s) => statusCounts[s]).map((s) => {
          const st = STATUS_STYLES[s];
          return (
            <Chip
              key={`summary-${s}`}
              size="small"
              label={`${st.label}: ${statusCounts[s]}`}
              sx={{
                color: st.color,
                backgroundColor: st.bg,
                border: `1px solid ${st.border}`,
                fontWeight: 600,
              }}
            />
          );
        })}
      </Box>

      <Box sx={{ mb: 2, p: 1 }}>
        {visibleLines.map((line) => {
          if (line.isOutcomeHeader) {
            return (
              <Box
                key={`viewer-header-${line.id}`}
                sx={{ ml: line.depth * 2, my: 0.5 }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#6c63ff", fontWeight: 600 }}
                >
                  {line.text}
                </Typography>
              </Box>
            );
          }

          // normal rule line
          const isSelected = selectedNodeId === line.id;
          const status = getStatus(line.node, line.text);
          const st = STATUS_STYLES[status];

          return (
            <Box
              key={`viewer-${line.id}`}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                ml: Math.max(0, line.depth) * 2,
                my: 0.5,
                py: 0.75,
                px: 1,
                borderRadius: 1,
                backgroundColor: st.bg,
                border: `1px solid ${st.border}`,
                borderLeft: `4px solid ${st.color}`,
                outline: isSelected ? `2px solid ${st.color}` : "none",
                outlineOffset: 1,
              }}
            >
              <Tooltip title="Click to focus this rule in the editor" arrow>
                <Button
                  size="small"
                  onClick={() =>
                    onSelectNode &&
                    onSelectNode({ id: line.id, numbering: line.numbering })
                  }
                  variant={isSelected ? "contained" : "outlined"}
                  sx={{
                    minWidth: 32,
                    height: 28,
                    fontSize: "0.8rem",
                    lineHeight: 1,
                    flexShrink: 0,
                    color: isSelected ? "#fff" : st.color,
                    borderColor: st.color,
                    backgroundColor: isSelected ? st.color : "transparent",
                    "&:hover": {
                      borderColor: st.color,
                      backgroundColor: isSelected ? st.color : st.border,
                    },
                  }}
                >
                  Rule - {line.numbering || "•"}
                </Button>
              </Tooltip>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Chip
                    size="small"
                    label={st.label}
                    sx={{
                      height: 22,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#fff",
                      backgroundColor: st.color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: "0.9rem",
                      color: st.color,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {line.text}
                  </Typography>
                </Box>

                {/* Display error information if present */}
                {line.node?.error && (
                  <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                    <AlertTitle sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Error
                    </AlertTitle>
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      {line.node.error}
                    </Typography>
                  </Alert>
                )}

                {/* Display traceback if present */}
                {line.node?.traceback?.traceback && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.5,
                      backgroundColor: "#fff3e0",
                      border: "1px solid #ff9800",
                      borderRadius: 1,
                      maxWidth: "100%",
                      overflow: "auto",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: "#e65100",
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      Traceback:
                    </Typography>
                    <Typography
                      component="pre"
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        color: "#d84315",
                        margin: 0,
                      }}
                    >
                      {line.node.traceback.traceback}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </PaperWrapper>
  );
};

export default NodesArrayViewer;
